import { proxy } from 'valtio';
import { ERROR_MESSAGES } from '@/constants/messages';
import { handleError } from '@/utils/helpers';
import { deleteHistory, getHistorySplit, generateTitle, updateHistoryTitle } from '@/services/api';

export interface HistoryItem<T = any> {
    title?: string | null;
    result: T | null;
    id: string;
    createTime: number;
}

export interface HistoryList<T = any> {
    data: HistoryItem<T>[][];
    isLoading: boolean;
    titleLoadingMap: Record<string, boolean>;
    runFetch: (userId: string | undefined) => Promise<void>;
    delItem: (userId: string | undefined, id: string | undefined) => Promise<void>;
    genTitle: (userId: string | undefined, id: string | undefined) => Promise<void>;
    reNameTitle: (userId: string | undefined, id: string | undefined, title: string) => Promise<void>;
}

/**
 * 更新指定ID的历史记录项的标题
 */
const updateItemTitle = (id: string, title: string): boolean => {
    for (let i = 0; i < HistoryStore.data.length; i++) {
        for (let j = 0; j < HistoryStore.data[i].length; j++) {
            if (HistoryStore.data[i][j].id === id) {
                HistoryStore.data[i][j].title = title;
                return true;
            }
        }
    }
    return false;
};

export const HistoryStore = proxy<HistoryList<any>>({
    data: [[], [], []],
    isLoading: false,
    titleLoadingMap: {},
    async runFetch(userId) {
        if (!userId) {
            handleError(null, ERROR_MESSAGES.USER_ID_ERROR);
            return;
        }
        HistoryStore.isLoading = true;

        try {
            const response = await getHistorySplit(userId);
            if (response.data.success) {
                HistoryStore.data = response.data.data;
            } else {
                handleError(response.data.data.message);
            }
        } catch (error) {
            handleError(error);
        } finally {
            HistoryStore.isLoading = false;
        }
    },

    async delItem(userId, id) {
        if (!userId || !id) {
            handleError(null, ERROR_MESSAGES.USER_ID_ERROR);
            return;
        }
        HistoryStore.isLoading = true;

        try {
            const response = await deleteHistory(userId, id);
            if (response.data.success) {
                HistoryStore.data = response.data.data;
            } else {
                handleError(response.data.data.message);
            }
        } catch (error) {
            handleError(error);
        } finally {
            HistoryStore.isLoading = false;
        }
    },

    async genTitle(userId, id) {
        if (!userId || !id) {
            handleError(null, ERROR_MESSAGES.USER_ID_ERROR);
            return;
        }

        HistoryStore.titleLoadingMap[id] = true;

        try {
            const response = await generateTitle(id);
            if (response.data.success) {
                const title = response.data.data;
                updateItemTitle(id, title);
            } else {
                handleError(response.data.data?.message || '生成标题失败');
            }
        } catch (error) {
            handleError(error);
        } finally {
            delete HistoryStore.titleLoadingMap[id];
        }
    },

    async reNameTitle(userId, id, title) {
        if (!userId || !id || !title) {
            handleError(null, ERROR_MESSAGES.USER_ID_ERROR);
            return;
        }

        HistoryStore.titleLoadingMap[id] = true;

        try {
            const response = await updateHistoryTitle(id, title);
            if (response.data.success) {
                updateItemTitle(id, title);
            } else {
                handleError(response.data.message || '更新标题失败');
            }
        } catch (error) {
            handleError(error);
        } finally {
            delete HistoryStore.titleLoadingMap[id];
        }
    }
});
